import { useEffect, useState } from 'react';
import {
    Box, Typography, Button, Stack, Alert, CircularProgress, Card, CardContent,
    IconButton, Tooltip, Chip, Pagination, Rating,
} from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import RefreshIcon from '@mui/icons-material/Refresh';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { api } from '../api/client';
import { ApiImageThumb } from '../components/ApiImage';


/**
 * Moderācijas rinda atsauksmēm uz Unverified vietām (Google/OSM/Refugee/Foursquare).
 * Identifikācija ar (userId, source, externalId) - source ir avots, externalId ir
 * avota nedalītais ID (Google place_id, OSM 'osm-n12345' utt.).
 *
 * Server endpointi:
 *   POST /api/v1/admin/moderation/external-reviews/pending
 *   POST /api/v1/admin/moderation/external-reviews/decide
 */
type PendingItem = {
    userId: string;
    source: string;
    externalId: string;
    star: number;
    description: string | null;
    placeAddress: string | null;
    createdAt: string;
    photos?: PhotoRef[];
};

/** Foto pie atsauksmes; accepted null = gaida „Ārējo foto moderācijā". */
type PhotoRef = {
    imageId: string;
    url: string;
    accepted: boolean | null;
};

type PendingResponse = {
    isSuccess: boolean;
    error: string | null;
    totalPending: number;
    items: PendingItem[];
};


const PAGE_SIZE = 20;


export function ExternalReviewsModerationPage() {
    const queryClient = useQueryClient();
    const [page, setPage] = useState(1);
    const [busyKey, setBusyKey] = useState<string | null>(null);

    const skip = (page - 1) * PAGE_SIZE;

    const { data, isLoading, error, refetch, isFetching } = useQuery({
        queryKey: ['admin-external-review-moderation', skip],
        queryFn: async () => {
            const resp = await api.post<PendingResponse>(
                '/api/v1/admin/moderation/external-reviews/pending',
                { skip, take: PAGE_SIZE },
            );
            return resp.data;
        },
        staleTime: 30_000,
    });

    const decideMutation = useMutation({
        mutationFn: async (args: { userId: string; source: string; externalId: string; accepted: boolean }) => {
            const resp = await api.post<{ isSuccess: boolean; error: string | null }>(
                '/api/v1/admin/moderation/external-reviews/decide',
                args,
            );
            if (!resp.data.isSuccess) {
                throw new Error(resp.data.error || 'Lēmums neizdevās.');
            }
            return args;
        },
        onMutate: (args) => setBusyKey(`${args.userId}:${args.source}:${args.externalId}`),
        onSettled: () => setBusyKey(null),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['admin-external-review-moderation'] });
        },
    });

    const items = data?.items ?? [];
    const totalPending = data?.totalPending ?? 0;
    const pageCount = Math.max(1, Math.ceil(totalPending / PAGE_SIZE));

    useEffect(() => {
        if (!isLoading && items.length === 0 && page > 1) {
            setPage((p) => Math.max(1, p - 1));
        }
    }, [items.length, isLoading, page]);

    return (
        <Box>
            <Stack direction="row" alignItems="center" justifyContent="space-between" mb={2}>
                <Box>
                    <Typography variant="h4">Unverified atsauksmju moderācija</Typography>
                    <Typography variant="body2" color="text.secondary">
                        Atsauksmes uz ārējiem WC avotiem (Google, OpenStreetMap, Refugee, Foursquare).
                        Apstiprinātie teksti kļūst publiski; noraidītie paliek paslēpti.
                        Zvaigžņu rating ir publiski neatkarīgi no šī.
                    </Typography>
                </Box>
                <Stack direction="row" spacing={2} alignItems="center">
                    <Chip
                        label={`Kopā gaida: ${totalPending}`}
                        color={totalPending > 0 ? 'warning' : 'default'}
                    />
                    <Tooltip title="Atjaunot">
                        <span>
                            <IconButton onClick={() => refetch()} disabled={isFetching}>
                                <RefreshIcon />
                            </IconButton>
                        </span>
                    </Tooltip>
                </Stack>
            </Stack>

            {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                    Neizdevās ielādēt moderācijas rindu: {String((error as Error).message)}
                </Alert>
            )}

            {isLoading && (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                    <CircularProgress />
                </Box>
            )}

            {!isLoading && items.length === 0 && (
                <Alert severity="success">
                    Šobrīd nav nekā, ko moderēt. Visi Unverified-vietu teksti ir apstrādāti.
                </Alert>
            )}

            <Stack spacing={1.5}>
                {items.map((it) => {
                    const key = `${it.userId}:${it.source}:${it.externalId}`;
                    return (
                        <Card key={key}>
                            <CardContent>
                                <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ md: 'flex-start' }}>
                                    <Box sx={{ flex: 1, minWidth: 0 }}>
                                        <Stack direction="row" spacing={1} alignItems="center" mb={0.5}>
                                            <Rating value={it.star} max={5} readOnly size="small" />
                                            <Chip label={it.source} size="small" variant="outlined" />
                                            <Typography variant="caption" color="text.secondary">
                                                par {it.placeAddress ?? '(nezināma adrese)'}
                                            </Typography>
                                        </Stack>
                                        <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap', mb: 1 }}>
                                            {it.description}
                                        </Typography>
                                        {!!it.photos?.length && (
                                            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap mb={1}>
                                                {it.photos.map((p) => (
                                                    <Box key={p.imageId} sx={{ position: 'relative' }}>
                                                        <ApiImageThumb url={p.url} size={120} alt="review photo" />
                                                        <Chip
                                                            size="small"
                                                            label={p.accepted == null ? 'gaida' : p.accepted ? 'apstiprināts' : 'noraidīts'}
                                                            color={p.accepted == null ? 'warning' : p.accepted ? 'success' : 'error'}
                                                            sx={{ position: 'absolute', left: 4, bottom: 4, pointerEvents: 'none' }}
                                                        />
                                                    </Box>
                                                ))}
                                            </Stack>
                                        )}
                                        <Stack direction="row" spacing={2}>
                                            <Typography variant="caption" color="text.secondary">
                                                {new Date(it.createdAt).toLocaleString('lv-LV')}
                                            </Typography>
                                            <Tooltip title={it.userId}>
                                                <Typography
                                                    variant="caption"
                                                    color="text.secondary"
                                                    sx={{ fontFamily: 'monospace' }}
                                                    noWrap
                                                >
                                                    Autors: {it.userId.slice(0, 8)}…
                                                </Typography>
                                            </Tooltip>
                                            <Tooltip title={it.externalId}>
                                                <Typography
                                                    variant="caption"
                                                    color="text.secondary"
                                                    sx={{ fontFamily: 'monospace' }}
                                                    noWrap
                                                >
                                                    ID: {it.externalId.slice(0, 12)}…
                                                </Typography>
                                            </Tooltip>
                                        </Stack>
                                    </Box>
                                    <Stack direction="row" spacing={1}>
                                        <Button
                                            color="success"
                                            variant="contained"
                                            startIcon={<CheckCircleOutlineIcon />}
                                            disabled={busyKey === key}
                                            onClick={() => decideMutation.mutate({
                                                userId: it.userId,
                                                source: it.source,
                                                externalId: it.externalId,
                                                accepted: true,
                                            })}
                                        >
                                            Apstiprināt
                                        </Button>
                                        <Button
                                            color="error"
                                            variant="outlined"
                                            startIcon={<HighlightOffIcon />}
                                            disabled={busyKey === key}
                                            onClick={() => decideMutation.mutate({
                                                userId: it.userId,
                                                source: it.source,
                                                externalId: it.externalId,
                                                accepted: false,
                                            })}
                                        >
                                            Noraidīt
                                        </Button>
                                    </Stack>
                                </Stack>
                            </CardContent>
                        </Card>
                    );
                })}
            </Stack>

            {pageCount > 1 && (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                    <Pagination
                        page={page}
                        count={pageCount}
                        onChange={(_, p) => setPage(p)}
                        showFirstButton
                        showLastButton
                    />
                </Box>
            )}
        </Box>
    );
}
